import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {AppStateService} from '../../shared/app-state.service';
import {AbstractControl, FormBuilder, FormGroup, ValidationErrors} from '@angular/forms';
import {ModalComponent} from '../../shared/modal/modal.component';
import {StarsRequired} from '../../shared/stars/stars.component';
import {ClientApiService, QccInfoResponse} from '../client-api.service';
import {HttpError} from '../../shared/api.service';
import {map} from 'rxjs/operators';
import {AuthService} from '../../shared/auth.service';
import {BehaviorSubject, Observable} from 'rxjs';
import {Router} from '@angular/router';
import {IdentifyModalComponent} from '../../shared/identify-modal/identify-modal.component';

@Component({
  selector: 'qcc-modal',
  templateUrl: './qcc-modal.component.html',
  styleUrls: ['../styles/qcc-modal.scss', './qcc-modal.component.scss']
})
export class QccModalComponent implements OnInit, OnChanges {

  @Input() dismissible = true;
  @Input() hidden = true;
  @Input() needsVerification = false;
  @Input() rating: number;
  @Input() order: OrderOrQccResponse;
  @Input() qccInfoResponse: QccInfoResponse;

  @Output() success = new EventEmitter<void>();

  @ViewChild('modal') modal: ModalComponent;
  @ViewChild('identifyModal') identifyModal: IdentifyModalComponent;

  form: FormGroup;
  loading$ = new BehaviorSubject(false);
  success$ = new BehaviorSubject(false);
  error$ = new BehaviorSubject(false);
  errorCode$ = new BehaviorSubject<string | null>(null);
  showQccTerms$ = new BehaviorSubject(false);
  brand$ = this.AppState.brand$;
  masquerading$ = this.Auth.masquerading$;
  unauthenticated$ = this.Auth.user$.pipe(map(user => !user));
  isAustralia$ = this.AppState.isAustralia$;

  private defaultFormValue = {shareFeedback: true};

  constructor(private AppState: AppStateService,
              private formBuilder: FormBuilder,
              private Auth: AuthService,
              private Api: ClientApiService,
              private Router: Router) {
  }

  ngOnInit() {
    if (this.qccInfoResponse) {
      this.order = {
        icName: this.qccInfoResponse.orders[0].icName,
        talent: this.qccInfoResponse.orders[0].talent,
        incompleteQccId: null
      };
    }

    this.initializeForm();

    if (!this.hidden) {
      this.show();
    }
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      rating: [this.rating, StarsRequired],
      notes: [''],
      shareFeedback: [true],
      companyRating: [0, (control: AbstractControl): ValidationErrors | null => {
        // Only required if companyNotes has a value
        return this.form && this.companyNotes.value ? StarsRequired(control) : null;
      }],
      companyNotes: ['']
    });

    this.companyNotes.valueChanges.subscribe(() => this.companyRating.updateValueAndValidity());

    if (this.qccInfoResponse) {
      this.form.patchValue({
        rating: this.rating || this.qccInfoResponse.orders[0].rating,
        notes: this.qccInfoResponse.orders[0].notes,
        companyRating: this.qccInfoResponse.companyRating,
        companyNotes: this.qccInfoResponse.companyNotes,
        shareFeedback: this.qccInfoResponse.orders[0].shareFeedback
      });
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['order'] && this.form) this.form.reset(this.defaultFormValue);
    if (changes['rating'] && this.form) this.form.patchValue({rating: this.rating});
  }

  show(rating?: number, order?: Order) {
    if (order) {
      this.order = order;
      this.form.reset(this.defaultFormValue);
    }
    if (rating) {
      this.rating = rating;
      this.form.patchValue({rating: this.rating});
    }
    this.modal.show();
  }

  hide(): void {
    this.modal.hide();
  }

  onHide() {
    if (this.success$.getValue()) this.success.emit(null);
  }

  submit() {
    if (this.masquerading$.getValue()) {
      return;
    }

    this.error$.next(false);
    this.errorCode$.next(null);
    this.loading$.next(true);

    if (this.qccInfoResponse) {
      this.saveQccReplies(this.form.getRawValue());
    } else {
      this.saveQccReply(this.form.getRawValue());
    }
  }

  saveQccReply(data: any) {
    this.Api.saveQccReply(this.order.incompleteQccId, {
      rating: data.rating,
      notes: data.notes,
      shareFeedback: data.shareFeedback,
      companyRating: data.companyRating,
      companyNotes: data.companyNotes
    }).subscribe(() => {
      this.loading$.next(false);
      this.success$.next(true);
    }, (err: HttpError) => this.handleError('Failed to save QCC reply', err));
  }

  saveQccReplies(data: any) {
    this.Api.saveQccReplies(this.qccInfoResponse.token, {
      replies: [{
        id: this.qccInfoResponse.orders[0].id,
        order: this.qccInfoResponse.orders[0].order,
        rating: data.rating,
        notes: data.notes,
        shareFeedback: data.shareFeedback
      }],
      companyRating: data.companyRating,
      companyNotes: data.companyNotes
    }, true, this.needsVerification).subscribe(() => {
      this.loading$.next(false);
      this.success$.next(true);
    }, (err: HttpError) => this.handleError('Failed to save QCC replies', err));
  }

  handleError(message: string, err: HttpError) {
    console.error(message, err);
    this.loading$.next(false);
    this.error$.next(true);
    this.errorCode$.next(err.code);
  }

  identifyCallback() {
    if (this.Auth.isFullyAuthenticated()) {
      this.Router.navigate(['/positions']);
    }
  }

  showPositions() {
    this.modal.hide();
    this.identifyModal.show();
  }

  toggleFeedbackTerms(event) {
    event.preventDefault();
    this.showQccTerms$.next(!this.showQccTerms$.getValue());
  }

  get talentRating() {
    return this.form.get('rating');
  }

  get companyNotes() {
    return this.form.get('companyNotes');
  }

  get companyRating() {
    return this.form.get('companyRating');
  }
}

interface OrderOrQccResponse {
  talent: PersonName;
  icName: string | null;
  incompleteQccId: number | null;
}
